import {
  enumCurrencyCode,
  enumSalesLoadLineItemPhase,
  enumUnit,
  enumWeightDisplayUnit,
  SalesLoadLineItemPhase,
} from '@rematter/frontend-lib-api-client';
import { uuid } from '@rematter/ui-web';
import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { createLoadAtom } from '../_graphql/mutationLoadCreate';
import { salesOrderUpdateAtom } from '../_graphql/mutationSalesOrderUpdate';
import { inventoryItemsQueryAtom } from '../_graphql/queryInventoryItems';
import { itemDescriptionsQueryAtom } from '../_graphql/queryInventoryItemsDescriptions';
import { inventoryTagsQueryAtom } from '../_graphql/queryInventoryTags';
import { salesOrderLineItemsListAtom } from '../_graphql/querySalesOrderLineItemsList';
import {
  enumLoadWeightType,
  GroupedCatalyticItems,
  InventoryItemRecord,
  LoadFormLineItem,
  LoadFormState,
  LoadLineItemRecord,
  LoadRecord,
} from '../types/load';
import {
  groupCatalyticItems,
  isCatalyticConverter,
} from '../utils/catalyticConverter';
import {
  createTagToLineItemMap,
  mapLoadLineItemsToFormLineItems,
} from '../utils/loadLineItems';
import { toMoney } from '../utils/money';
import { convertSalesOrderQuantityToWeight } from '../utils/weight';

const INVENTORY_ITEMS_QUERY_LIMIT = 200;

export const initialState = (): LoadFormState => ({
  bookingId: undefined,
  salesOrderId: undefined,
  facilityId: undefined,
  currencyCode: enumCurrencyCode.USD,
  expectedShipDate: undefined,
  status: undefined,
  weightDisplayUnit: enumWeightDisplayUnit.PriceUnit,
  internalNotes: '',
  externalNotes: '',
  shippingCarrierName: undefined,
  scac: undefined,
  freightForwarder: undefined,
  truckFreight: undefined,
  deliveryNumber: undefined,
  releaseNumber: undefined,
  bookingNumber: undefined,
  driverName: undefined,
  truckNumber: undefined,
  trailerNumber: undefined,
  containerNumber: undefined,
  salesOrderNumber: undefined,
  sealNumber: undefined,
  outboundTollingAgreementId: undefined,
  lineItems: [],
});

export const $loadForm = atom<LoadFormState>(initialState());

// 🔘🔘🔘 PLAIN ATOMS - Baseline form state for discard functionality
// This tracks the "saved" state that discard should reset to
export const $discardBaseline = atom<LoadFormState | null>(null);

// 🔘🔘🔘 PLAIN ATOMS - Save success indicator
// Set to true on successful save, auto-cleared by component after timeout
export const $showSaveSuccess = atom(false);

// Fields to ignore when comparing line items for unsaved changes
const IGNORED_LINE_ITEM_KEYS: (keyof LoadFormLineItem)[] = ['phase', 'id'];

const compareLineItems = (
  formItems: LoadFormLineItem[],
  baselineItems: LoadFormLineItem[],
): boolean => {
  if (formItems.length !== baselineItems.length) return false;

  const baselineById = new Map(baselineItems.map((item) => [item.id, item]));

  return formItems.every((formItem) => {
    const baselineItem = baselineById.get(formItem.id);
    if (!baselineItem) return false;

    const formKeys = Object.keys(formItem) as (keyof LoadFormLineItem)[];
    return formKeys.every((key) => {
      if (IGNORED_LINE_ITEM_KEYS.includes(key)) return true;
      return isEqual(formItem[key], baselineItem[key]);
    });
  });
};

// 🟢🟢🟢 DERIVED DATA -> Check if form has unsaved changes compared to baseline
export const $hasUnsavedChanges = atom((get) => {
  const form = get($loadForm);
  const baseline = get($discardBaseline);

  if (!baseline) return false;

  const formKeys = Object.keys(form) as (keyof LoadFormState)[];

  for (const key of formKeys) {
    if (key === 'lineItems') {
      if (!compareLineItems(form.lineItems, baseline.lineItems ?? [])) {
        return true;
      }
      continue;
    }
    if (!isEqual(form[key], baseline[key])) return true;
  }

  return false;
});

// 🔘🔘🔘 PLAIN ATOMS - Store the mapping of tagId to original lineItemId
export const $tagToLineItemMap = atom<Map<string, string>>(new Map());

// 🟠🟠🟠 ACTION -> RESET load form to initialState
export const $resetLoadForm = atom(null, (get, set) => {
  set($loadForm, initialState());
  set($tagToLineItemMap, new Map());
});

const mapLoadToLoadForm = (load: LoadRecord | LoadFormState) => {
  const lineItems = mapLoadLineItemsToFormLineItems(load.lineItems);

  return {
    bookingId: load.bookingId,
    salesOrderId: load.salesOrderId,
    salesOrderNumber: load.salesOrderNumber,
    facilityId: load.facilityId,
    currencyCode: load.currencyCode ?? enumCurrencyCode.USD,
    expectedShipDate: load.expectedShipDate,
    shippedDate: load.shippedDate,
    loadReceivedDate: load.loadReceivedDate,
    status: load.status,
    weightDisplayUnit: load.weightDisplayUnit,
    heavyWeight: load.heavyWeight,
    lightWeight: load.lightWeight,
    internalNotes: load.internalNotes || '',
    externalNotes: load.externalNotes || '',
    shippingCarrierName: load.shippingCarrierName,
    scac: load.scac,
    freightForwarder: load.freightForwarder,
    truckFreight: load.truckFreight,
    deliveryNumber: load.deliveryNumber,
    releaseNumber: load.releaseNumber,
    bookingNumber: load.bookingNumber,
    driverName: load.driverName,
    truckNumber: load.truckNumber,
    trailerNumber: load.trailerNumber,
    containerTypeId: load.containerTypeId,
    containerNumber: load.containerNumber,
    containerId: load.containerId,
    sealNumber: load.sealNumber,
    outboundTollingAgreementId: load.outboundTollingAgreementId,
    lineItems,
  };
};

// 🟠🟠🟠 ACTION -> UPDATE tags to use in mapLoadToLoadForm
const $setLineItemsTagIds = atom(
  null,
  (get, set, load: LoadRecord | LoadFormState) => {
    const tagToLineItemMap = createTagToLineItemMap(
      load.lineItems as LoadLineItemRecord[],
    );
    set($tagToLineItemMap, tagToLineItemMap);
  },
);

// 🟠🟠🟠 ACTION -> RESET load form with current state (discard functionality)
export const $resetLoadFormToLoad = atom(null, (get, set, _) => {
  const baseline = get($discardBaseline);
  if (baseline) {
    set($loadForm, baseline);
    set($selectedSalesOrderId, baseline.salesOrderId ?? undefined);
    set($selectedBookingId, baseline.bookingId ?? undefined);
  }
  // Note: We don't reset $setLineItemsTagIds since baseline already has the correct lineItems
  // and tag mapping is only needed for save operations
});

// 🟠🟠🟠 ACTION -> RESET load form with shipment info
export const $resetLoadFormToShipmentInfo = atom(
  null,
  (get, set, load: LoadRecord | LoadFormState) => {
    const loadFormState: LoadFormState = {
      status: load.status,
      weightDisplayUnit: load.weightDisplayUnit,
      internalNotes: load.internalNotes || '',
      externalNotes: load.externalNotes || '',
      shippingCarrierName: load.shippingCarrierName,
      scac: load.scac,
      freightForwarder: load.freightForwarder,
      truckFreight: load.truckFreight,
      deliveryNumber: load.deliveryNumber,
      releaseNumber: load.releaseNumber,
      bookingNumber: load.bookingNumber,
      driverName: load.driverName,
      truckNumber: load.truckNumber,
      trailerNumber: load.trailerNumber,
      containerTypeId: load.containerTypeId,
      containerNumber: load.containerNumber,
      containerId: load.containerId,
      sealNumber: load.sealNumber,
      lineItems: [],
    };
    set($loadForm, loadFormState);
  },
);

// 🟠🟠🟠 ACTION -> UPDATE load form with updated fields
export const $updateLoadForm = atom(
  null,
  (get, set, update: Partial<LoadFormState>) => {
    set($loadForm, { ...get($loadForm), ...update });
  },
);

// 🟠🟠🟠 ACTION -> UPDATE load form
export const $populateLoadFormFromLoad = atom(
  null,
  (get, set, load: LoadRecord | LoadFormState) => {
    const formState = mapLoadToLoadForm(load);
    // Update form
    set($loadForm, formState);
    set($setLineItemsTagIds, load);
    // Set initial baseline for discard
    set($discardBaseline, formState);

    // Update stable ID atoms
    set($selectedSalesOrderId, load.salesOrderId ?? undefined);
    set($selectedBookingId, load.bookingId ?? undefined);
  },
);

// 🟠🟠🟠 ACTION -> POPULATE load form with tracking
export const $populateLoadFormAction = atom(
  null,
  (get, set, load: LoadRecord | LoadFormState | null | undefined) => {
    if (!load) return;

    const currentLoadId = 'id' in load ? load.id : undefined;
    const lastLoadId = get($lastPopulatedLoadId);

    // Reset the flag when we encounter a new load
    if (currentLoadId !== lastLoadId) {
      set($isFormPopulated, false);
    }

    // Only populate if not already populated
    if (!get($isFormPopulated)) {
      set($populateLoadFormFromLoad, load);
      set($lastPopulatedLoadId, currentLoadId);
      set($isFormPopulated, true);
    }
  },
);

// Separate atoms for stable IDs to prevent unnecessary re-queries
export const $selectedSalesOrderId = atom<string | undefined>(undefined);
export const $selectedBookingId = atom<string | undefined>(undefined);

// 🔘🔘🔘 PLAIN ATOMS - Form Population Tracking
export const $lastPopulatedLoadId = atom<string | undefined>(undefined);
export const $isFormPopulated = atom<boolean>(false);

// 🔘🔘🔘 PLAIN ATOMS - Sales Order Sync Tracking
export const $previousLoadSalesOrderId = atom<string | undefined>(undefined);
export const $hasInitialSync = atom<boolean>(false);
export const $addedLineItemIds = atom<Set<string>>(new Set<string>());
export const $previousSalesOrderItemIds = atom<Set<string>>(new Set<string>());

// 🔘🔘🔘 PLAIN ATOMS
export const $loadMaterialsStatus = atom<SalesLoadLineItemPhase>(
  enumSalesLoadLineItemPhase.Requested,
);

// 🟢🟢🟢 REMOTE DATA -> sales order line items
export const $salesOrderLineItems = salesOrderLineItemsListAtom((get) => ({
  variables: { salesOrderId: get($selectedSalesOrderId) ?? '' },
  skip: !get($selectedSalesOrderId),
}));

// 🟢🟢🟢 REMOTE DATA -> inventory items
export const $inventoryItems = inventoryItemsQueryAtom(() => ({
  variables: {
    limit: INVENTORY_ITEMS_QUERY_LIMIT,
  },
}));

// 🟢🟢🟢 DERIVED DATA -> inventory items by ID lookup
export const $inventoryItemsById = atom<Record<string, InventoryItemRecord>>(
  (get) => {
    const { data: itemsData } = get($inventoryItems);
    return itemsData?.itemsById ?? {};
  },
);

// 🟢🟢🟢 DERIVED DATA -> catalytic converter items filtered from inventory
export const $catalyticItems = atom<InventoryItemRecord[]>((get) => {
  const { data: itemsData } = get($inventoryItems);
  if (!itemsData?.allItems) return [];

  return itemsData.allItems
    .filter((item) => isCatalyticConverter(item.category?.name))
    .map((item) => ({
      id: item.id,
      name: item.name,
      mnemonic: item.mnemonic,
      itemGroupId: item.itemGroupId,
      itemGroup: item.itemGroup,
      categoryId: item.categoryId,
      category: {
        name: item.category?.name ?? '',
      },
      metadata: item.metadata,
    }));
});

// 🟢🟢🟢 DERIVED DATA -> grouped catalytic items for modal display and size resolution
export const $groupedCatalyticItems = atom<GroupedCatalyticItems>((get) => {
  const catalyticItems = get($catalyticItems);
  return groupCatalyticItems(catalyticItems);
});

// 🟢🟢🟢 DERIVED DATA -> representative items for modal grid display (one per group)
export const $catalyticRepresentativeItems = atom<InventoryItemRecord[]>(
  (get) => {
    const grouped = get($groupedCatalyticItems);
    return grouped.representativeItems;
  },
);

// 🟢🟢🟢 REMOTE DATA -> inventory items
export const $itemDescriptions = itemDescriptionsQueryAtom((get) => ({
  variables: {
    materialIds: get($loadForm)
      .lineItems.map((item) => item.itemId)
      .filter((id): id is string => id !== undefined && id !== null),
  },
  skip: get($loadForm).lineItems.every((item) => item.itemId === undefined),
}));

// 🟢🟢🟢 REMOTE DATA -> inventory tags
export const $inventoryTags = inventoryTagsQueryAtom(() => ({
  variables: {},
}));

export const $loadFormLineItems = atom((get) => get($loadForm).lineItems);

export const $lineItemsByPhaseKey = atom<
  Record<SalesLoadLineItemPhase, LoadFormLineItem[]>
>((get) => {
  const lineItems = get($loadFormLineItems);

  if (lineItems.length === 0) {
    return {
      [enumSalesLoadLineItemPhase.Requested]: [],
      [enumSalesLoadLineItemPhase.Staged]: [],
      [enumSalesLoadLineItemPhase.Shipped]: [],
      [enumSalesLoadLineItemPhase.Received]: [],
      [enumSalesLoadLineItemPhase.CustomerReceived]: [],
    };
  }

  if (lineItems.some((item) => !item.phase)) {
    return {
      [enumSalesLoadLineItemPhase.Requested]: lineItems.filter(
        (item) => !item.phase,
      ),
      [enumSalesLoadLineItemPhase.Staged]: [],
      [enumSalesLoadLineItemPhase.Shipped]: [],
      [enumSalesLoadLineItemPhase.Received]: [],
      [enumSalesLoadLineItemPhase.CustomerReceived]: [],
    };
  }

  return {
    [enumSalesLoadLineItemPhase.Requested]: lineItems.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.Requested,
    ),
    [enumSalesLoadLineItemPhase.Staged]: lineItems.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.Staged,
    ),
    [enumSalesLoadLineItemPhase.Shipped]: lineItems.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
    ),
    [enumSalesLoadLineItemPhase.Received]: lineItems.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.Received,
    ),
    [enumSalesLoadLineItemPhase.CustomerReceived]: lineItems.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.CustomerReceived,
    ),
  };
});

// 🟠🟠🟠 ACTION -> UPDATE sales order
export const $updateSalesOrder = salesOrderUpdateAtom({
  onSuccess: (get) => {
    // Refetch line items after successful update
    get($salesOrderLineItems).refetch();
  },
});

/**
 * ACTION -> CREATE load
 * @deprecated Use dedicated create load atom instead
 */
export const $createLoad = createLoadAtom();

// 🟠🟠🟠 ACTION -> SYNC sales order line items
export const $syncSalesOrderLineItemsAction = atom(
  null,
  (
    get,
    set,
    params: {
      loadSalesOrderId?: string | null;
      onSyncComplete?: (
        lineItems: LoadFormLineItem[],
        currentLoadForm: LoadFormState,
      ) => void;
    },
  ) => {
    const { loadSalesOrderId, onSyncComplete } = params;

    const form = get($loadForm);

    // Don't run sync if form hasn't been populated yet (only for existing loads with ID)
    // For new loads (no ID), we can sync immediately
    if (form.id && !get($isFormPopulated)) {
      return;
    }

    const salesOrderLineItemsQuery = get($salesOrderLineItems);
    const isFetchingSalesOrderLineItems = salesOrderLineItemsQuery.isLoading;

    // Don't run sync if we're fetching sales order line items
    if (isFetchingSalesOrderLineItems) {
      return;
    }

    if (!loadSalesOrderId) {
      set($previousLoadSalesOrderId, undefined);
      set($hasInitialSync, false);
      set($addedLineItemIds, new Set());
      set($previousSalesOrderItemIds, new Set());
      return;
    }

    const previousLoadSalesOrderId = get($previousLoadSalesOrderId);
    const hasInitialSync = get($hasInitialSync);

    // Only run sync if:
    // 1. This is the first sync after form population, OR
    // 2. The load.salesOrderId has changed (after save/refetch)
    const shouldSync =
      !hasInitialSync || previousLoadSalesOrderId !== loadSalesOrderId;

    if (!shouldSync) {
      return;
    }

    const salesOrderLineItems =
      salesOrderLineItemsQuery.data?.salesOrders.entries[0]?.lineItems ?? [];

    if (salesOrderLineItems.length === 0) {
      return;
    }

    const currentLineItems = form.lineItems ?? [];

    const isSalesOrderChange =
      hasInitialSync &&
      previousLoadSalesOrderId &&
      previousLoadSalesOrderId !== loadSalesOrderId;

    let updatedLineItems = [...currentLineItems];

    // If sales order changed (not initial sync), delete items from previous sales order
    if (isSalesOrderChange) {
      const previousSalesOrderItemIds = get($previousSalesOrderItemIds);
      updatedLineItems = updatedLineItems.filter((lineItem) => {
        if (!lineItem.itemId) return true;
        return !previousSalesOrderItemIds.has(lineItem.itemId);
      });
    }

    const existingLineItemsByItemId = new Map(
      updatedLineItems
        .filter((item) => item.itemId)
        .map((item) => [item.itemId as string, item]),
    );

    const salesOrderLineItemsByItemId = new Map(
      salesOrderLineItems.map((lineItem) => [lineItem.item.id, lineItem]),
    );

    const salesOrderItemIds = new Set(
      salesOrderLineItems.map((lineItem) => lineItem.item.id),
    );
    const hasExistingLineItemsFromSalesOrder = updatedLineItems.some(
      (lineItem) => lineItem.itemId && salesOrderItemIds.has(lineItem.itemId),
    );

    // Update existing line items that match sales order line items
    updatedLineItems = updatedLineItems.map((lineItem) => {
      if (!lineItem.itemId) return lineItem;

      const matchingSalesOrderLine = salesOrderLineItemsByItemId.get(
        lineItem.itemId,
      );

      if (!matchingSalesOrderLine) return lineItem;

      const quantityOwedWeight = convertSalesOrderQuantityToWeight(
        Number(matchingSalesOrderLine.quantity),
        matchingSalesOrderLine.unit,
      );

      return {
        ...lineItem,
        includeInSalesOrder: false,
        unitPrice: toMoney(matchingSalesOrderLine.unitPrice),
        pricingUnit: matchingSalesOrderLine.unit,
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.QuantityOwed]: quantityOwedWeight,
        },
      };
    });

    // Add new line items from sales order only if:
    // 1. Sales order changed (isSalesOrderChange) - add new SO items after removing old ones, OR
    // 2. No existing line items from this sales order - user hasn't worked with this SO yet
    // If user already has some items from this SO, they control what to add via the UI
    const shouldAddNewLineItems =
      isSalesOrderChange || !hasExistingLineItemsFromSalesOrder;

    const addedLineItemIds = get($addedLineItemIds);
    const newLineItems: LoadFormLineItem[] = [];
    const newAddedIds = new Set(addedLineItemIds);

    if (shouldAddNewLineItems) {
      salesOrderLineItems.forEach((salesOrderLine) => {
        const itemId = salesOrderLine.item.id;

        if (existingLineItemsByItemId.has(itemId) || newAddedIds.has(itemId)) {
          return;
        }

        const quantityOwedWeight = convertSalesOrderQuantityToWeight(
          Number(salesOrderLine.quantity),
          salesOrderLine.unit,
        );

        newLineItems.push({
          type: 'Raw' as const,
          id: `so-${uuid()}`,
          itemId: itemId,
          tagId: null,
          unitPrice: toMoney(salesOrderLine.unitPrice),
          pricingUnit: salesOrderLine.unit,
          salesOrderLineId: salesOrderLine.id,
          includeInSalesOrder: false,
          weights: {
            [enumLoadWeightType.Gross]: {
              value: 0,
              unit: enumUnit.Pounds,
            },
            [enumLoadWeightType.Tare]: {
              value: 0,
              unit: enumUnit.Pounds,
            },
            [enumLoadWeightType.Net]: {
              value: 0,
              unit: enumUnit.Pounds,
            },
            [enumLoadWeightType.QuantityOwed]: quantityOwedWeight,
          },
        });

        newAddedIds.add(itemId);
      });

      if (newAddedIds.size > addedLineItemIds.size) {
        set($addedLineItemIds, newAddedIds);
      }
    }

    updatedLineItems = [...updatedLineItems, ...newLineItems];

    // Update form with batched changes
    set($loadForm, {
      ...form,
      lineItems: updatedLineItems,
    });

    // Update tracking state
    set($previousLoadSalesOrderId, loadSalesOrderId);
    set($hasInitialSync, true);

    const newPreviousSalesOrderItemIds = new Set(
      salesOrderLineItems.map((item) => item.item.id),
    );
    set($previousSalesOrderItemIds, newPreviousSalesOrderItemIds);

    const currentLoadForm = get($loadForm);

    // Update baseline after sync to reflect synced state
    set($discardBaseline, currentLoadForm);

    // Call onSyncComplete callback if sales order changed
    if (isSalesOrderChange && onSyncComplete) {
      onSyncComplete(updatedLineItems, currentLoadForm);
    }
  },
);
